import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CssBaseline, Container } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import { theme } from './theme/theme'

import { initializeApp } from './features/app/appSlice';
import QuoteProcessPage from './pages/QuoteProcessPage';
import Spinner from './components/ph/Spinner/Spinner';
import ErrorPage from './features/utils/ErrorPage';
import ErrorDataContact from './features/utils/ErrorDataContact';

const App = () => {
  const dispatch = useDispatch();
  const { status: appStatus } = useSelector((state) => state.app);
  const { status: insuranceStatus } = useSelector((state) => state.insurance);
  const { updateQuoteStatus } = useSelector((state) => state.quote);

  const {
    status: coverageStatus,
    error: coverageError,
    postPackageStatus
  } = useSelector((state) => state.coverage);

  const isZeroPremiumError = coverageError === 'ZERO_PREMIUM';

  useEffect(() => {

    const loadApp = async () => {
      const delay = new Promise(resolve => setTimeout(resolve, 5000));
      await Promise.all([dispatch(initializeApp()), delay]);
    };

    loadApp();
  }, [dispatch]);

  useEffect(() => {
    const isRNWebView =
      !!window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === 'function';

    if (isRNWebView) {
      return;
    }

    const target = document.getElementById('root') ?? document.body;
    const RESIZE_THRESHOLD_PX = 16;

    let lastWidth = 0;
    let lastHeight = 0;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        const h = Math.round(entry.contentRect.height);

        const widthDelta = Math.abs(w - lastWidth);
        const heightDelta = Math.abs(h - lastHeight);

        if (widthDelta <= RESIZE_THRESHOLD_PX && heightDelta <= RESIZE_THRESHOLD_PX) {
          continue;
        }

        lastWidth = w;
        lastHeight = h;

        const message = {
          timestamp: new Date().toISOString(),
          type: 'IFRAME_RESIZE',
          payload: { width: w, height: h }
        };

        const t = window.parent !== window ? window.parent : window;
        console.log(
          '[ResizeObserver] enviando a',
          t === window ? 'self (sin iframe)' : 'window.parent (iframe)',
          '->',
          message
        );
        t.postMessage(JSON.stringify(message), '*');
      }
    });

    resizeObserver.observe(target);
    return () => {
      console.log('[ResizeObserver] desconectando observer (unmount de App)');
      resizeObserver.disconnect();
    };
  }, []);
  
  const hasCriticalError =
    appStatus === 'error' ||
    coverageStatus === 'failed' ||
    postPackageStatus === 'failed' ||
    updateQuoteStatus === 'failed' ||
    insuranceStatus === 'failed';

  const isLoading = appStatus === 'loading' || postPackageStatus === 'loading' ||
    updateQuoteStatus === 'loading';

  if (isLoading) {
    return (
      <Spinner
        variant="yellow"
        size={60}
      />
    );
  }

  if (isZeroPremiumError) {
    return <ErrorDataContact />;
  }

  if (hasCriticalError) {
    return <ErrorDataContact />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QuoteProcessPage />
    </ThemeProvider>
  );
};

export default App;